import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './App.css';

// Generate 50 fictional companies with fun emoji hints
const generateCompanies = () => {
  const companyNames = [
    "Zentrix Inc", "Nebulo Corp", "Fintora Labs", "Quantum Dynamics", "Stellar Tech",
    "CyberNova", "BioSynth Solutions", "Neural Networks Co", "SolarFlux Energy", "AquaTech Marine",
    "RoboLogic Systems", "GreenEarth Renewables", "DataSphere Analytics", "MegaCorp Industries", "FutureTech Ventures",
    "Cosmic Computing", "EcoSmart Solutions", "Digital Dreams Inc", "Innovation Labs", "TechTitan Corp",
    "SpaceXplorer", "BioGen Research", "CloudNine Systems", "SmartGrid Energy", "Virtual Reality Pro",
    "AI Advancements", "Blockchain Bros", "CryptoCorp", "DroneTech Solutions", "Electric Motors Inc",
    "Fusion Power Co", "Genetic Engineering Labs", "Hologram Systems", "Internet of Things Corp", "JetPack Aviation",
    "Kinetic Energy Solutions", "Laser Technologies", "Magnetic Levitation Co", "Nanotech Innovations", "Orbital Space Systems",
    "Plasma Technologies", "Quantum Computing Corp", "Robotic Automation", "Satellite Communications", "Teleportation Tech",
    "Underwater Exploration", "Virtual Intelligence", "Wearable Tech Solutions", "Xenon Power Systems", "Youth Enhancement Labs"
  ];

  const hints = [
    "🚀 A leader in lunar mining tech", "🌌 Pioneering interstellar travel", "💰 Revolutionary fintech solutions",
    "⚛️ Quantum computing breakthroughs", "⭐ Space tourism pioneers", "🤖 Advanced AI development",
    "🧬 Genetic engineering experts", "🧠 Neural network specialists", "☀️ Solar energy innovators",
    "🌊 Underwater city builders", "🤖 Robotics automation leaders", "🌱 Green energy revolutionaries",
    "📊 Big data analytics masters", "🏭 Industrial automation experts", "🔮 Future technology visionaries",
    "🌌 Cosmic computing solutions", "♻️ Eco-friendly innovations", "💭 Digital transformation experts",
    "🔬 Research and development hub", "⚡ Tech industry titans", "🚀 Space exploration pioneers",
    "🧬 Biotech research leaders", "☁️ Cloud computing experts", "⚡ Smart grid technology",
    "🥽 Virtual reality specialists", "🤖 Artificial intelligence pioneers", "🔗 Blockchain technology experts",
    "₿ Cryptocurrency innovators", "🚁 Drone technology leaders", "⚡ Electric vehicle pioneers",
    "⚛️ Fusion power researchers", "🧬 Genetic modification experts", "👻 Holographic display tech",
    "📱 IoT connectivity specialists", "🛩️ Personal aviation solutions", "⚡ Kinetic energy harvesters",
    "🔦 Laser technology experts", "🧲 Magnetic levitation pioneers", "🔬 Nanotechnology researchers",
    "🛰️ Orbital space systems", "⚡ Plasma technology innovators", "⚛️ Quantum computing leaders",
    "🤖 Robotic automation experts", "📡 Satellite communication", "🚪 Teleportation technology",
    "🌊 Underwater exploration tech", "🧠 Virtual intelligence systems", "⌚ Wearable technology",
    "⚡ Xenon power systems", "🧬 Youth enhancement research"
  ];

  // Generate initial price history for each company
  const generateInitialPriceHistory = () => {
    const history = [];
    let currentPrice = Math.floor(Math.random() * 491) + 10; // Random starting price between 10-500
    
    for (let i = 0; i < 10; i++) {
      // Random movement between -30% and +30% for initial history
      const movement = (Math.random() - 0.5) * 60;
      const multiplier = 1 + (movement / 100);
      currentPrice = Math.max(10, Math.min(500, Math.round(currentPrice * multiplier)));
      history.push(currentPrice);
    }
    
    return history;
  };

  return companyNames.map((name, index) => {
    const priceHistory = generateInitialPriceHistory();
    const currentPrice = priceHistory[priceHistory.length - 1];
    const highestPrice = Math.max(...priceHistory);
    const lowestPrice = Math.min(...priceHistory);
    
    return {
      id: index + 1,
      name,
      hint: hints[index],
      currentPrice,
      priceHistory,
      lastPrice: priceHistory[priceHistory.length - 2] || currentPrice,
      highestPrice,
      lowestPrice,
      trend: Math.random() > 0.5 ? 'up' : 'down'
    };
  });
};

// Circular Progress Bar Component
const CircularProgress = ({ percentage, size = 80, strokeWidth = 8, label }) => {
  // Clamp percentage to [-100, 100]
  const clamped = Math.max(-100, Math.min(100, percentage));
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const absPercent = Math.abs(clamped);
  const arcLength = (absPercent / 100) * circumference;
  const strokeDasharray = `${arcLength} ${circumference}`;
  const color = clamped > 0 ? '#28a745' : clamped < 0 ? '#dc3545' : '#adb5bd';
  const rotation = clamped >= 0 ? -90 : 90;

  return (
    <div className="circular-progress">
      <svg width={size} height={size} className="circular-progress-svg">
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc */}
        {absPercent > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={0}
            strokeLinecap="round"
            transform={`rotate(${rotation} ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 0.4s, stroke 0.4s' }}
          />
        )}
      </svg>
      <div className="circular-progress-text">
        {label && <span className="circular-progress-label">{label}</span>}
        <span className={`percentage ${clamped > 0 ? 'positive' : clamped < 0 ? 'negative' : ''}`}>{clamped > 0 ? '+' : ''}{clamped.toFixed(2)}%</span>
      </div>
    </div>
  );
};

// Linear Progress Bar Component
const LinearProgress = ({ current, min, max, label, color = "#667eea" }) => {
  const percentage = ((current - min) / (max - min)) * 100;
  
  return (
    <div className="linear-progress">
      <div className="progress-label">
        <span>{label}</span>
        <span className="progress-value">₹{current.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
      </div>
      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill"
          style={{ 
            width: `${percentage}%`,
            backgroundColor: color
          }}
        />
      </div>
      <div className="progress-range">
        <span>₹{min.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
        <span>₹{max.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
      </div>
    </div>
  );
};

// Half Circular Progress Bar Component
const HalfCircularProgress = ({ percentage, size = 100, strokeWidth = 8, color = "#667eea", label = "" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="half-circular-progress">
      <svg width={size} height={size / 2} className="half-circular-svg">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={circumference / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset + circumference / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeLinecap="round"
        />
      </svg>
      <div className="half-circular-text">
        <div className="half-circular-percentage">{percentage.toFixed(2)}%</div>
        {label && <div className="half-circular-label">{label}</div>}
      </div>
    </div>
  );
};

// Interactive stock chart component with hover tooltips
const StockChart = ({ priceHistory, companyName, onPointClick, onInvest, onSell, isInvested, currentPrice, userCoins, ownedShares, onBuyClick, onSellClick }) => {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [stockQuantity, setStockQuantity] = useState(1);
  const [sellQuantity, setSellQuantity] = useState(1);
  const [showAdModal, setShowAdModal] = useState(false);
  const maxPrice = Math.max(...priceHistory);
  const minPrice = Math.min(...priceHistory);
  const range = maxPrice - minPrice || 1;
  
  const points = priceHistory.map((price, index) => {
    const x = (index / (priceHistory.length - 1)) * 100;
    const y = 100 - ((price - minPrice) / range) * 100;
    return { x, y, price, index };
  });

  const handlePointClick = (event, point) => {
    event.stopPropagation();
    setSelectedPoint(selectedPoint?.index === point.index ? null : point);
    if (onPointClick) {
      onPointClick(point);
    }
  };

  const handleChartClick = () => {
    setSelectedPoint(null);
  };

  const handleInvestClick = () => {
    setShowInvestmentModal(true);
    setStockQuantity(1);
  };

  const handleSellClick = () => {
    setShowSellModal(true);
    setSellQuantity(1);
  };

  const handleBuyStocks = () => {
    const totalCost = stockQuantity * currentPrice;
    if (totalCost <= userCoins) {
      onInvest && onInvest(stockQuantity);
      setShowInvestmentModal(false);
    } else {
      setShowAdModal(true);
    }
  };

  const handleSellStocks = () => {
    if (sellQuantity <= ownedShares) {
      onSell && onSell(sellQuantity);
      setShowSellModal(false);
    }
  };

  const handleWatchAd = () => {
    // Simulate watching an ad and getting 100 coins
    const newCoins = userCoins + 100;
    // Update coins in parent component
    if (onInvest) {
      onInvest(stockQuantity, newCoins);
    }
    setShowAdModal(false);
    setShowInvestmentModal(false);
  };

  const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div className="stock-chart">
      <h4>{companyName} Stock Price</h4>
      <div className="chart-container">
        <svg 
          width="100%" 
          height="150" 
          viewBox="0 0 100 100" 
          className="chart-svg"
          onClick={handleChartClick}
        >
          <polyline
            fill="none"
            stroke="#667eea"
            strokeWidth="2"
            points={polylinePoints}
          />
          
          {/* Clickable points */}
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="5"
              fill="transparent"
              stroke="transparent"
              className="clickable-point"
              onClick={(e) => handlePointClick(e, point)}
            />
          ))}
          
          {/* Current point */}
          <circle
            cx={points[points.length - 1].x}
            cy={points[points.length - 1].y}
            r="3"
            fill="#667eea"
            stroke="white"
            strokeWidth="1"
          />
          
          {/* Selected point indicator */}
          {selectedPoint && (
            <g>
              <circle
                cx={selectedPoint.x}
                cy={selectedPoint.y}
                r="4"
                fill="#667eea"
                stroke="white"
                strokeWidth="2"
              />
              <rect
                x={selectedPoint.x + 5}
                y={selectedPoint.y - 35}
                width="80"
                height="30"
                fill="#333"
                opacity="0.95"
                rx="5"
              />
              <text
                x={selectedPoint.x + 10}
                y={selectedPoint.y - 20}
                fill="white"
                fontSize="9"
                fontFamily="Arial, sans-serif"
              >
                ₹{selectedPoint.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </text>
              <text
                x={selectedPoint.x + 10}
                y={selectedPoint.y - 10}
                fill="#ccc"
                fontSize="7"
                fontFamily="Arial, sans-serif"
              >
                Point {selectedPoint.index + 1}
              </text>
            </g>
          )}
        </svg>
        
        {/* Price change indicators */}
        <div className="price-change-info">
          {selectedPoint && (
            <div className="tooltip-info">
              <div className="tooltip-row">
                <span>Price:</span>
                <span className="tooltip-value">₹{selectedPoint.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="tooltip-row">
                <span>Point:</span>
                <span className="tooltip-value">{selectedPoint.index + 1} of {priceHistory.length}</span>
              </div>
              {selectedPoint.index > 0 && (
                <div className="tooltip-row">
                  <span>Change:</span>
                  <span className={`tooltip-value ${priceHistory[selectedPoint.index] > priceHistory[selectedPoint.index - 1] ? 'positive' : 'negative'}`}>
                    {priceHistory[selectedPoint.index] > priceHistory[selectedPoint.index - 1] ? '+' : ''}
                    {priceHistory[selectedPoint.index] - priceHistory[selectedPoint.index - 1]}
                    ({((priceHistory[selectedPoint.index] - priceHistory[selectedPoint.index - 1]) / priceHistory[selectedPoint.index - 1] * 100).toFixed(2)}%)
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Investment actions */}
        <div className="investment-actions">
          {!isInvested ? (
            <button 
              className="invest-btn professional-buy-btn"
              onClick={onBuyClick}
            >
              <span className="btn-icon">📈</span>
              <span className="btn-text">Buy {companyName}</span>
            </button>
          ) : (
            <button 
              className="sell-btn professional-sell-btn"
              onClick={onSellClick}
            >
              <span className="btn-icon">💸</span>
              <span className="btn-text">Sell {companyName}</span>
            </button>
          )}
        </div>

        {/* Investment Modal */}
        {showInvestmentModal && (
          <div className="modal-overlay" onClick={() => setShowInvestmentModal(false)}>
            <div className="investment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Buy {companyName} Stocks</h3>
                <button 
                  className="modal-close"
                  onClick={() => setShowInvestmentModal(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="stock-info">
                  <p>Current Price: ₹{currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <p>Your Coins: ₹{userCoins.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
                <div className="quantity-selector">
                  <label>Number of Stocks:</label>
                  <div className="quantity-controls">
                    <button 
                      className="quantity-btn"
                      onClick={() => setStockQuantity(Math.max(1, stockQuantity - 1))}
                    >
                      -
                    </button>
                    <span className="quantity-display">{stockQuantity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    <button 
                      className="quantity-btn"
                      onClick={() => setStockQuantity(stockQuantity + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="cost-info">
                  <p>Total Cost: ₹{(stockQuantity * currentPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <p className={`budget-status ${stockQuantity * currentPrice <= userCoins ? 'sufficient' : 'insufficient'}`}>
                    {stockQuantity * currentPrice <= userCoins ? '✅ Sufficient funds' : '❌ Insufficient funds'}
                  </p>
                </div>
                <div className="modal-actions">
                  <button 
                    className="buy-btn"
                    onClick={handleBuyStocks}
                    disabled={stockQuantity * currentPrice > userCoins}
                  >
                    Buy {stockQuantity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} Stock{stockQuantity > 1 ? 's' : ''}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={() => setShowInvestmentModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ad Modal */}
        {showAdModal && (
          <div className="modal-overlay" onClick={() => setShowAdModal(false)}>
            <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>💰 Need More Coins?</h3>
                <button 
                  className="modal-close"
                  onClick={() => setShowAdModal(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="ad-content">
                  <p>You don't have enough coins to buy {stockQuantity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} stock{stockQuantity > 1 ? 's' : ''}.</p>
                  <p>Total needed: ₹{(stockQuantity * currentPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <p>Your balance: ₹{userCoins.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  <div className="ad-offer">
                    <p>🎬 Watch a short ad to get 100 coins!</p>
                    <button 
                      className="watch-ad-btn"
                      onClick={handleWatchAd}
                    >
                      📺 Watch Ad & Get 100 Coins
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="chart-labels">
          <span className="min-price">₹{minPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          <span className="max-price">₹{maxPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [coins, setCoins] = useState(1000);
  const [portfolio, setPortfolio] = useState({});
  const [currentCompanies, setCurrentCompanies] = useState([]);
  const [gameHistory, setGameHistory] = useState([]);
  const [allCompanies, setAllCompanies] = useState(generateCompanies());
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('your-stocks');
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [isPlayingRound, setIsPlayingRound] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [buyQuantity, setBuyQuantity] = useState(1);
  const [sellQuantity, setSellQuantity] = useState(1);
  const [selectedCompanyForTrade, setSelectedCompanyForTrade] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  useEffect(() => {
    const savedCoins = localStorage.getItem('gamistocks_coins');
    const savedPortfolio = localStorage.getItem('gamistocks_portfolio');
    const savedGameHistory = localStorage.getItem('gamistocks_history');
    const savedCompanies = localStorage.getItem('gamistocks_companies');
    const savedTheme = localStorage.getItem('gamistocks_theme');

    const hasSavedData = savedCoins || savedPortfolio || savedGameHistory || savedCompanies;

    if (hasSavedData) {
      const coinsVal = savedCoins ? parseInt(savedCoins) : 1000;
      const portfolioVal = savedPortfolio ? JSON.parse(savedPortfolio) : {};
      const gameHistoryVal = savedGameHistory ? JSON.parse(savedGameHistory) : [];
      const companiesVal = savedCompanies ? JSON.parse(savedCompanies) : generateCompanies();
      const themeVal = savedTheme ? JSON.parse(savedTheme) : false;

      setCoins(coinsVal);
      setPortfolio(portfolioVal);
      setGameHistory(gameHistoryVal);
      setAllCompanies(companiesVal);
      setIsDarkTheme(themeVal);

      const sortedCompanies = [...companiesVal].sort((a, b) => {
        const aChange = calculatePercentageChange(a);
        const bChange = calculatePercentageChange(b);
        return bChange - aChange;
      });
      const topGainers = sortedCompanies.slice(0, 5);
      const topLosers = sortedCompanies.slice(-5).reverse();
      setCurrentCompanies([...topGainers, ...topLosers]);
    } else {
      setCoins(1000);
      setPortfolio({});
      setGameHistory([]);
      const companies = generateCompanies();
      setAllCompanies(companies);
      setIsDarkTheme(true);
      generateNewRound();
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem('gamistocks_coins', coins.toString());
    localStorage.setItem('gamistocks_portfolio', JSON.stringify(portfolio));
    localStorage.setItem('gamistocks_history', JSON.stringify(gameHistory));
    localStorage.setItem('gamistocks_companies', JSON.stringify(allCompanies));
    localStorage.setItem('gamistocks_theme', JSON.stringify(isDarkTheme));
  }, [coins, portfolio, gameHistory, allCompanies, isDarkTheme, loaded]);

  useEffect(() => {
    document.body.className = isDarkTheme ? 'dark-theme' : '';
  }, [isDarkTheme]);

  useEffect(() => {
    if (selectedCompany) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = 'auto';
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.style.overflow = 'auto';
      document.body.classList.remove('modal-open');
    };
  }, [selectedCompany]);

  const generateNewRound = () => {
    const sortedCompanies = [...allCompanies].sort((a, b) => {
      const aChange = calculatePercentageChange(a);
      const bChange = calculatePercentageChange(b);
      return bChange - aChange;
    });
    
    const topGainers = sortedCompanies.slice(0, 5);
    const topLosers = sortedCompanies.slice(-5).reverse();
    
    setCurrentCompanies([...topGainers, ...topLosers]);
  };

  const simulateStockMovement = (company) => {
    // Randomly increase or decrease price by up to 40%
    const percentChange = (Math.random() * 0.8 - 0.4); // -0.4 to +0.4
    const movement = company.currentPrice * percentChange;
    return movement;
  };

  const updateCompanyPrice = (company) => {
    const movement = simulateStockMovement(company);
    const newPrice = Math.max(10, Math.min(500, Math.round(company.currentPrice + movement)));
    const percentageChange = ((newPrice - company.currentPrice) / company.currentPrice) * 100;
    
    setAllCompanies(prev => prev.map(c => {
      if (c.id === company.id) {
        const updatedCompany = {
          ...c,
          lastPrice: c.currentPrice,
          currentPrice: newPrice,
          priceHistory: [...c.priceHistory, newPrice].slice(-20),
          trend: movement > 0 ? 'up' : 'down',
          highestPrice: Math.max(c.highestPrice, newPrice),
          lowestPrice: Math.min(c.lowestPrice, newPrice)
        };
        return updatedCompany;
      }
      return c;
    }));
    
    return { movement, percentageChange };
  };

  const playRound = () => {
    // Add button click effect
    setIsPlayingRound(true);
    
    // Simulate processing time for better UX
    setTimeout(() => {
      const results = [];
      const portfolioUpdates = [];

      allCompanies.forEach(company => {
        const { movement, percentageChange } = updateCompanyPrice(company);
        
        const investment = portfolio[company.id];
        if (investment) {
          const oldValue = investment.shares * (company.currentPrice - movement);
          const newValue = investment.shares * company.currentPrice;
          const roundProfit = newValue - oldValue;
          
          portfolioUpdates.push({
            companyId: company.id,
            companyName: company.name,
            shares: investment.shares,
            oldPrice: company.currentPrice - movement,
            newPrice: company.currentPrice,
            movement: movement,
            percentageChange: percentageChange,
            roundProfit: roundProfit,
            totalInvested: investment.totalInvested,
            totalValue: newValue,
            totalProfit: newValue - investment.totalInvested
          });
        }
        
        if (currentCompanies.find(c => c.id === company.id)) {
          results.push({
            company: company.name,
            movement: movement,
            percentageChange: percentageChange,
            newPrice: company.currentPrice
          });
        }
      });

      setGameHistory(prev => [...prev, { 
        round: gameHistory.length + 1, 
        results, 
        portfolioUpdates,
        totalReturn: portfolioUpdates.reduce((sum, update) => sum + update.roundProfit, 0)
      }]);
      
      generateNewRound();
      
      // Show alert that stock values have changed
      const totalReturn = portfolioUpdates.reduce((sum, update) => sum + update.roundProfit, 0);
      const alertMessage = `🎮 Round ${gameHistory.length + 1} Complete!\n\n📈 Stock values have been updated!\n💰 Portfolio change: ${totalReturn >= 0 ? '+' : ''}₹${totalReturn.toFixed(2)}\n\nCheck the Market Movers section to see the biggest changes!`;
      
      alert(alertMessage);
      
      // Reset button effect
      setIsPlayingRound(false);
    }, 500);
  };

  const handleInvestInCompany = (company, quantity = 1, newCoins = null) => {
    const investmentAmount = quantity * company.currentPrice;
    const currentCoins = newCoins !== null ? newCoins : coins;
    
    if (currentCoins >= investmentAmount) {
      setCoins(prev => newCoins !== null ? newCoins : prev - investmentAmount);
      setPortfolio(prev => ({
        ...prev,
        [company.id]: {
          shares: quantity,
          totalInvested: investmentAmount,
          name: company.name
        }
      }));
      
      setGameHistory(prev => [...prev, {
        round: gameHistory.length + 1,
        type: 'buy',
        transaction: {
          company: company.name,
          shares: quantity,
          price: company.currentPrice,
          amount: investmentAmount
        },
        results: [],
        portfolioUpdates: [],
        totalReturn: 0
      }]);
      
      return true;
    }
    return false;
  };

  const sellShares = (companyId, quantity = null) => {
    const investment = portfolio[companyId];
    if (investment) {
      const company = allCompanies.find(c => c.id === parseInt(companyId));
      
      if (!company) {
        console.error(`Company with ID ${companyId} not found`);
        return;
      }
      
      const sharesToSell = quantity || investment.shares;
      
      if (sharesToSell <= investment.shares) {
        const currentValue = sharesToSell * company.currentPrice;
        const profit = currentValue - (investment.totalInvested * (sharesToSell / investment.shares));
        const avgBuyPrice = investment.totalInvested / investment.shares;
        
        setCoins(prev => prev + currentValue);
        
        if (sharesToSell === investment.shares) {
          setPortfolio(prev => {
            const newPortfolio = { ...prev };
            delete newPortfolio[companyId];
            return newPortfolio;
          });
        } else {
          const remainingShares = investment.shares - sharesToSell;
          const remainingInvestment = investment.totalInvested * (remainingShares / investment.shares);
          
          setPortfolio(prev => ({
            ...prev,
            [companyId]: {
              ...investment,
              shares: remainingShares,
              totalInvested: remainingInvestment
            }
          }));
        }
        
        setGameHistory(prev => [...prev, {
          round: gameHistory.length + 1,
          type: 'sell',
          transaction: {
            company: company.name,
            shares: sharesToSell,
            buyPrice: avgBuyPrice,
            sellPrice: company.currentPrice,
            profit: profit,
            percentageChange: ((company.currentPrice - avgBuyPrice) / avgBuyPrice * 100)
          },
          results: [],
          portfolioUpdates: [],
          totalReturn: profit
        }]);
      }
    }
  };

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
  };

  const handleSellClick = (companyId) => {
    const company = allCompanies.find(c => c.id === parseInt(companyId));
    const investment = portfolio[companyId];
    if (company && investment) {
      setSelectedCompanyForTrade(company);
      setSellQuantity(1);
      setShowSellModal(true);
    }
  };

  const handleSellConfirm = () => {
    if (selectedCompanyForTrade && sellQuantity > 0) {
      const investment = portfolio[selectedCompanyForTrade.id];
      if (investment && sellQuantity <= investment.shares) {
        sellShares(selectedCompanyForTrade.id, sellQuantity);
        setShowSellModal(false);
        setSelectedCompanyForTrade(null);
        setSellQuantity(1);
      }
    }
  };

  const handleBuyClick = (company) => {
    setSelectedCompanyForTrade(company);
    setBuyQuantity(1);
    setShowBuyModal(true);
  };

  const handleBuyConfirm = () => {
    if (selectedCompanyForTrade && buyQuantity > 0) {
      const success = handleInvestInCompany(selectedCompanyForTrade, buyQuantity);
      if (success) {
        setShowBuyModal(false);
        setSelectedCompanyForTrade(null);
        setBuyQuantity(1);
      }
    }
  };

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  const calculatePercentageChange = (company) => {
    if (company.lastPrice === company.currentPrice) return 0;
    return ((company.currentPrice - company.lastPrice) / company.lastPrice) * 100;
  };

  useEffect(() => {
    console.log('--- GamiStocks Local Storage ---');
    Object.keys(localStorage)
      .filter(key => key.startsWith('gamistocks'))
      .forEach(key => {
        console.log(key, localStorage.getItem(key));
      });
    console.log('-------------------------------');
  }, [coins, portfolio, gameHistory, allCompanies, isDarkTheme]);

  // Calculate total portfolio value (coins + value of stocks)
  const portfolioStockValue = Object.entries(portfolio).reduce((sum, [companyId, investment]) => {
    const company = allCompanies.find(c => c.id === parseInt(companyId));
    if (!company) return sum;
    return sum + investment.shares * company.currentPrice;
  }, 0);
  const totalPortfolioValue = coins + portfolioStockValue;

  // Add reset handler
  const handleResetGame = () => {
    if (window.confirm('Are you sure you want to reset your game? This will erase all progress.')) {
      // Clear all GamiStocks-related localStorage keys
      Object.keys(localStorage)
        .filter(key => key.startsWith('gamistocks'))
        .forEach(key => localStorage.removeItem(key));
      // Reset all state
      setCoins(1000);
      setPortfolio({});
      setGameHistory([]);
      const companies = generateCompanies();
      setAllCompanies(companies);
      setIsDarkTheme(true);
      setCurrentCompanies([]);
      setActiveTab('your-stocks');
      setShowHowToPlay(false);
      setSearchTerm("");
      // Generate new round
      setTimeout(() => generateNewRound(), 0);
    }
  };

  return (
    <div className={`App ${isDarkTheme ? 'dark-theme' : ''}`}>


      <div className="container">
        <header className="header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div className="header-top">
              <h1>🎮 GamiStocks</h1>
              <button 
                className="theme-toggle"
                onClick={toggleTheme}
                title={isDarkTheme ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
              >
                {isDarkTheme ? '☀️' : '🌙'}
              </button>
            </div>
            <div className="stats">
              <div className="stat">
                <span className="label">💰 Coins:</span>
                <span className="value">{coins.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="stat">
                <span className="label">📈 Portfolio Value:</span>
                <span className="value">{totalPortfolioValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a href="https://coff.ee/myselfsk" target="_blank" rel="noopener noreferrer" className="button coffee-button" style={{ background: 'linear-gradient(90deg, #ffdd00, #ff8800)', color: '#222', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: '1rem', boxShadow: '0 2px 8px rgba(255,221,0,0.08)', border: 'none', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                ☕ Buy me a coffee
              </a>
              <button className="button reset-button" onClick={handleResetGame} style={{ background: 'linear-gradient(90deg, #dc3545, #fd7e14)', color: 'white', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: '1rem', boxShadow: '0 2px 8px rgba(220,53,69,0.08)', border: 'none', cursor: 'pointer' }}>
                ♻️ Reset Game
              </button>
            </div>
          </div>
        </header>

        <nav className="nav-bar">
          <button 
            className={`nav-tab ${activeTab === 'your-stocks' ? 'active' : ''}`}
            onClick={() => { setActiveTab('your-stocks'); setShowHowToPlay(false); }}
          >
            📊 Your Stocks
          </button>
          <button 
            className={`nav-tab ${activeTab === 'all-stocks' ? 'active' : ''}`}
            onClick={() => { setActiveTab('all-stocks'); setShowHowToPlay(false); }}
          >
            📈 All Stocks
          </button>
          <button
            className={`nav-tab ${showHowToPlay ? 'active' : ''}`}
            onClick={() => { setShowHowToPlay(true); setActiveTab('how-to-play'); }}
          >
            ❓ How to Play
          </button>
        </nav>

        {/* Play Round button only if not in How to Play */}
        {!showHowToPlay && (
          <div className="play-round-section">
            <button 
              className={`play-round-button ${isPlayingRound ? 'playing' : ''}`}
              onClick={playRound}
              disabled={isPlayingRound}
            >
              {isPlayingRound ? '🎲 Processing...' : '🎮 Play Round'}
            </button>
          </div>
        )}

        {activeTab === 'your-stocks' && (
          <>
            {Object.keys(portfolio).length > 0 && (
              <div className="card">
                <h2>📊 Your Portfolio</h2>
                <div className="portfolio-grid">
                  {Object.entries(portfolio).map(([companyId, investment]) => {
                    const company = allCompanies.find(c => c.id === parseInt(companyId));
                    if (!company) return null;
                    
                    const currentValue = investment.shares * company.currentPrice;
                    const profit = currentValue - investment.totalInvested;
                    const profitPercentage = (profit / investment.totalInvested) * 100;
                    const avgBuyPrice = investment.totalInvested / investment.shares;
                    
                    return (
                      <div key={companyId} className="portfolio-item">
                        <div className="portfolio-header">
                          <h3>{company.name}</h3>
                          <span className="hint">{company.hint}</span>
                        </div>
                        
                        <div className="portfolio-details">
                          <div className="performance-indicator">
                            <CircularProgress 
                              percentage={calculatePercentageChange(company)} 
                              size={110} 
                              strokeWidth={12}
                              label="Change"
                            />
                          </div>

                          <div className="price-comparison">
                            <div className="price-bar">
                              <div className="price-label">Buy Price</div>
                              <LinearProgress 
                                current={avgBuyPrice}
                                min={Math.min(avgBuyPrice, company.currentPrice)}
                                max={Math.max(avgBuyPrice, company.currentPrice)}
                                label=""
                                color="#6c757d"
                              />
                              <div className="price-value">₹{avgBuyPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                            </div>
                            <div className="price-bar">
                              <div className="price-label">Current Price</div>
                              <LinearProgress 
                                current={company.currentPrice}
                                min={Math.min(avgBuyPrice, company.currentPrice)}
                                max={Math.max(avgBuyPrice, company.currentPrice)}
                                label=""
                                color={company.currentPrice >= avgBuyPrice ? "#28a745" : "#dc3545"}
                              />
                              <div className="price-value">₹{company.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                            </div>
                          </div>

                          <div className="investment-summary">
                            <div className="summary-grid">
                              <div className="summary-item">
                                <div className="summary-icon">📊</div>
                                <div className="summary-content">
                                  <div className="summary-label">Shares</div>
                                  <div className="summary-value">{investment.shares.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </div>
                              </div>
                              <div className="summary-item">
                                <div className="summary-icon">💰</div>
                                <div className="summary-content">
                                  <div className="summary-label">Invested</div>
                                  <div className="summary-value">₹{investment.totalInvested.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </div>
                              </div>
                              <div className="summary-item">
                                <div className="summary-icon">💎</div>
                                <div className="summary-content">
                                  <div className="summary-label">Current Value</div>
                                  <div className="summary-value">₹{currentValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </div>
                              </div>
                              <div className="summary-item">
                                <div className="summary-icon">📈</div>
                                <div className="summary-content">
                                  <div className="summary-label">P/L</div>
                                  <div className={`summary-value ${profit >= 0 ? 'positive' : 'negative'}`}>
                                    {profit >= 0 ? '+' : ''}₹{profit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="pl-progress">
                            <div className="pl-header">
                              <span className="pl-label">Profit/Loss Progress</span>
                              <span className={`pl-percentage ${calculatePercentageChange(company) >= 0 ? 'positive' : 'negative'}`}>
                                {calculatePercentageChange(company) >= 0 ? '+' : ''}
                                {calculatePercentageChange(company).toFixed(2)}%
                              </span>
                            </div>
                            <div className="pl-bar-container">
                              <div 
                                className={`pl-bar ${calculatePercentageChange(company) >= 0 ? 'positive' : 'negative'}`}
                                style={{ 
                                  width: `${Math.min(Math.abs(calculatePercentageChange(company)), 100)}%`,
                                  left: calculatePercentageChange(company) < 0 ? `${100 - Math.min(Math.abs(calculatePercentageChange(company)), 100)}%` : '0%'
                                }}
                              ></div>
                              <div className="pl-center-line"></div>
                            </div>
                            <div className="pl-labels">
                              <span className="pl-min">-100%</span>
                              <span className="pl-zero">0%</span>
                              <span className="pl-max">+100%</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="portfolio-actions">
                          <button 
                            className="button sell-button"
                            onClick={() => handleSellClick(companyId)}
                          >
                            💸 Sell Shares
                          </button>
                          <button 
                            className="button view-chart-button"
                            onClick={() => handleCompanyClick(company)}
                          >
                            📈 View Chart
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="card">
              <h2>📈 Market Movers</h2>
              <p className="round-info">Round {gameHistory.length + 1} - Top performers and biggest drops</p>
              
              <div className="market-section">
                <h3>🚀 Top Gainers</h3>
                <div className="grid">
                  {currentCompanies.slice(0, 5).map(company => {
                    const isInvested = portfolio[company.id];
                    const percentageChange = calculatePercentageChange(company);
                    
                    return (
                      <div key={company.id} className="company-card gainer">
                        <div className="company-header">
                          <h3 onClick={() => handleCompanyClick(company)} className="clickable-title">
                            {company.name}
                          </h3>
                          <span className="hint">{company.hint}</span>
                          <div className="price-info">
                            <p className="current-price">₹{company.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                            <CircularProgress 
                              percentage={percentageChange} 
                              size={110} 
                              strokeWidth={12}
                              color="#28a745"
                              label="Change"
                            />
                          </div>
                        </div>
                        
                        <div className="change-info">
                          <span className="percentage-change positive">
                            +{percentageChange.toFixed(2)}%
                          </span>
                        </div>
                        
                        {isInvested && (
                          <div className="invested-company">
                            <p>You own {isInvested.shares.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} shares</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="market-section">
                <h3>📉 Top Losers</h3>
                <div className="grid">
                  {currentCompanies.slice(5, 10).map(company => {
                    const isInvested = portfolio[company.id];
                    const percentageChange = calculatePercentageChange(company);
                    
                    return (
                      <div key={company.id} className="company-card loser">
                        <div className="company-header">
                          <h3 onClick={() => handleCompanyClick(company)} className="clickable-title">
                            {company.name}
                          </h3>
                          <span className="hint">{company.hint}</span>
                          <div className="price-info">
                            <p className="current-price">₹{company.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                            <CircularProgress 
                              percentage={percentageChange} 
                              size={110} 
                              strokeWidth={12}
                              color={percentageChange >= 0 ? "#28a745" : "#dc3545"}
                              label="Change"
                            />
                          </div>
                        </div>
                        
                        <div className="change-info">
                          <span className="percentage-change negative">
                            {percentageChange.toFixed(2)}%
                          </span>
                        </div>
                        
                        {isInvested && (
                          <div className="invested-company">
                            <p>You own {isInvested.shares.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} shares</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {gameHistory.length > 0 && (
              <div className="card">
                <h2>📜 Game History</h2>
                <div className="history-list">
                  {gameHistory.slice(-5).reverse().map((round, index) => (
                    <div key={round.round} className="history-item">
                      <div className="round-header">
                        <h3>Round {round.round}</h3>
                        <div className="round-summary">
                          <span className="round-total">
                            Portfolio Change: {round.totalReturn >= 0 ? '+' : ''}₹{round.totalReturn.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                      </div>
                      
                      {round.type === 'buy' && (
                        <div className="transaction-item buy-transaction">
                          <h4>🛒 Buy Transaction</h4>
                          <div className="transaction-details">
                            <div className="transaction-row">
                              <span className="label">Company:</span>
                              <span className="value">{round.transaction.company}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">Shares:</span>
                              <span className="value">{round.transaction.shares.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">Price:</span>
                              <span className="value">₹{round.transaction.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">Amount:</span>
                              <span className="value">₹{round.transaction.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {round.type === 'sell' && (
                        <div className="transaction-item sell-transaction">
                          <h4>💰 Sell Transaction</h4>
                          <div className="transaction-details">
                            <div className="transaction-row">
                              <span className="label">Company:</span>
                              <span className="value">{round.transaction.company}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">Shares:</span>
                              <span className="value">{round.transaction.shares.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">Buy Price:</span>
                              <span className="value">₹{round.transaction.buyPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">Sell Price:</span>
                              <span className="value">₹{round.transaction.sellPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">Profit/Loss:</span>
                              <span className={`value ${round.transaction.profit >= 0 ? 'positive' : 'negative'}`}>
                                {round.transaction.profit >= 0 ? '+' : ''}₹{round.transaction.profit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            <div className="transaction-row">
                              <span className="label">P/L %:</span>
                              <span className={`value ${round.transaction.profit >= 0 ? 'positive' : 'negative'}`}>
                                {Number(round.transaction.percentageChange) > 0 ? '+' : ''}
                                {Number(round.transaction.percentageChange).toFixed(2)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {round.portfolioUpdates && round.portfolioUpdates.length > 0 && (
                        <div className="portfolio-updates">
                          <h4>📊 Your Portfolio Updates</h4>
                          {round.portfolioUpdates.map((update, updateIndex) => (
                            <div key={updateIndex} className="portfolio-update-item">
                              <div className="update-header">
                                <span className="company-name">{update.companyName}</span>
                                <span className={`update-movement ${update.movement >= 0 ? 'positive' : 'negative'}`}>
                                  {update.movement >= 0 ? '+' : ''}
                                  {update.movement.toFixed(2)} ({update.percentageChange.toFixed(2)}%)
                                </span>
                              </div>
                              <div className="update-details">
                                <div className="update-row">
                                  <span className="label">Shares:</span>
                                  <span className="value">{update.shares.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                                <div className="update-row">
                                  <span className="label">Price Change:</span>
                                  <span className="value">₹{update.oldPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} → ₹{update.newPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                                <div className="update-row">
                                  <span className="label">Round P/L:</span>
                                  <span className={`value ${update.roundProfit >= 0 ? 'positive' : 'negative'}`}>
                                    {update.roundProfit >= 0 ? '+' : ''}₹{update.roundProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                                <div className="update-row">
                                  <span className="label">Total P/L:</span>
                                  <span className={`value ${update.totalProfit >= 0 ? 'positive' : 'negative'}`}>
                                    {update.totalProfit >= 0 ? '+' : ''}₹{update.totalProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="market-movers">
                        <h4>📈 Market Movers</h4>
                        <div className="round-results">
                          {round.results.map((result, resultIndex) => (
                            <div key={resultIndex} className="result-item">
                              <span className="company-name">{result.company}</span>
                              <span className={`movement ${result.movement >= 0 ? 'positive' : 'negative'}`}>
                                {result.movement >= 0 ? '+' : ''}
                                {result.movement.toFixed(2)}
                              </span>
                              <span className="percentage-change">
                                {Number(result.percentageChange) > 0 ? '+' : ''}
                                {Number(result.percentageChange).toFixed(2)}%
                              </span>
                              <span className="new-price">₹{result.newPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'all-stocks' && (
          <div className="card">
            <h2>📈 All 50 Companies</h2>
            {/* Search Bar */}
            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
              <input
                type="text"
                className="search-bar"
                placeholder="Search companies by name or hint..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', maxWidth: 400, padding: 12, borderRadius: 8, border: '1px solid #ccc', fontSize: '1rem' }}
              />
            </div>
            <div className="companies-grid">
              {allCompanies
                .filter(company =>
                  company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  company.hint.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(company => {
                  const percentageChange = calculatePercentageChange(company);
                  return (
                    <div key={company.id} className="company-listing">
                      <div 
                        className="company-name-clickable"
                        onClick={() => handleCompanyClick(company)}
                      >
                        <h3>{company.name}</h3>
                        <p className="hint">{company.hint}</p>
                        <div className="price-info">
                          <p className="current-price">₹{company.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                          <CircularProgress 
                            percentage={percentageChange} 
                            size={110} 
                            strokeWidth={12}
                            label="Change"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {showBuyModal && selectedCompanyForTrade && ReactDOM.createPortal(
          <div className="modal-overlay" onClick={() => setShowBuyModal(false)}>
            <div className="modal-content trade-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📈 Buy {selectedCompanyForTrade.name}</h2>
                <button className="modal-close" onClick={() => setShowBuyModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="trade-info">
                  <div className="company-info">
                    <h3>{selectedCompanyForTrade.name}</h3>
                    <p className="hint">{selectedCompanyForTrade.hint}</p>
                    <div className="current-price-info">
                      <span className="price-label">Current Price:</span>
                      <span className="price-value">₹{selectedCompanyForTrade.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  </div>
                </div>
                <div className="quantity-selector">
                  <label htmlFor="buyQuantity">Number of shares to buy:</label>
                  <div className="quantity-input-group">
                    <input
                      type="number"
                      id="buyQuantity"
                      min="1"
                      max={Math.floor(coins / selectedCompanyForTrade.currentPrice)}
                      value={buyQuantity}
                      onChange={(e) => setBuyQuantity(Math.max(1, Math.min(parseInt(e.target.value) || 1, Math.floor(coins / selectedCompanyForTrade.currentPrice))))}
                      className="quantity-input"
                    />
                    <div className="quantity-buttons">
                      <button 
                        onClick={() => setBuyQuantity(Math.max(1, buyQuantity - 1))}
                        className="quantity-btn"
                      >
                        -
                      </button>
                      <button 
                        onClick={() => setBuyQuantity(Math.min(Math.floor(coins / selectedCompanyForTrade.currentPrice), buyQuantity + 1))}
                        className="quantity-btn"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <div className="trade-summary">
                  <div className="summary-row">
                    <span className="label">Shares to buy:</span>
                    <span className="value">{buyQuantity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Total cost:</span>
                    <span className="value">₹{(buyQuantity * selectedCompanyForTrade.currentPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Remaining coins:</span>
                    <span className="value">₹{(coins - (buyQuantity * selectedCompanyForTrade.currentPrice)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                </div>
                <div className="modal-actions">
                  <button 
                    className="button cancel-button"
                    onClick={() => setShowBuyModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="button confirm-button"
                    onClick={handleBuyConfirm}
                    disabled={buyQuantity <= 0 || (buyQuantity * selectedCompanyForTrade.currentPrice) > coins}
                    style={{background: 'linear-gradient(135deg, #28a745, #20c997)'}}
                  >
                    📈 Confirm Purchase
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {showSellModal && selectedCompanyForTrade && ReactDOM.createPortal(
          <div className="modal-overlay" onClick={() => setShowSellModal(false)}>
            <div className="modal-content trade-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>💸 Sell {selectedCompanyForTrade.name}</h2>
                <button className="modal-close" onClick={() => setShowSellModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="trade-info">
                  <div className="company-info">
                    <h3>{selectedCompanyForTrade.name}</h3>
                    <p className="hint">{selectedCompanyForTrade.hint}</p>
                    <div className="current-price-info">
                      <span className="price-label">Current Price:</span>
                      <span className="price-value">₹{selectedCompanyForTrade.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  </div>
                  <div className="portfolio-info">
                    <div className="info-row">
                      <span className="label">Shares Owned:</span>
                      <span className="value">{portfolio[selectedCompanyForTrade.id]?.shares.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Total Invested:</span>
                      <span className="value">₹{portfolio[selectedCompanyForTrade.id]?.totalInvested.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Current Value:</span>
                      <span className="value">₹{(portfolio[selectedCompanyForTrade.id]?.shares * selectedCompanyForTrade.currentPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  </div>
                </div>
                <div className="quantity-selector">
                  <label htmlFor="sellQuantity">Number of shares to sell:</label>
                  <div className="quantity-input-group">
                    <input
                      type="number"
                      id="sellQuantity"
                      min="1"
                      max={portfolio[selectedCompanyForTrade.id]?.shares || 1}
                      value={sellQuantity}
                      onChange={(e) => setSellQuantity(Math.max(1, Math.min(parseInt(e.target.value) || 1, portfolio[selectedCompanyForTrade.id]?.shares || 1)))}
                      className="quantity-input"
                    />
                    <div className="quantity-buttons">
                      <button 
                        onClick={() => setSellQuantity(Math.max(1, sellQuantity - 1))}
                        className="quantity-btn"
                      >
                        -
                      </button>
                      <button 
                        onClick={() => setSellQuantity(Math.min(portfolio[selectedCompanyForTrade.id]?.shares || 1, sellQuantity + 1))}
                        className="quantity-btn"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <div className="trade-summary">
                  <div className="summary-row">
                    <span className="label">Shares to sell:</span>
                    <span className="value">{sellQuantity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Sale value:</span>
                    <span className="value">₹{(sellQuantity * selectedCompanyForTrade.currentPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Average buy price:</span>
                    <span className="value">₹{(portfolio[selectedCompanyForTrade.id]?.totalInvested / portfolio[selectedCompanyForTrade.id]?.shares).toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Profit/Loss:</span>
                    <span className={`value ${(sellQuantity * selectedCompanyForTrade.currentPrice - (portfolio[selectedCompanyForTrade.id]?.totalInvested * (sellQuantity / portfolio[selectedCompanyForTrade.id]?.shares))) >= 0 ? 'positive' : 'negative'}`}>
                      {((sellQuantity * selectedCompanyForTrade.currentPrice - (portfolio[selectedCompanyForTrade.id]?.totalInvested * (sellQuantity / portfolio[selectedCompanyForTrade.id]?.shares))) >= 0 ? '+' : '')}
                      ₹{(sellQuantity * selectedCompanyForTrade.currentPrice - (portfolio[selectedCompanyForTrade.id]?.totalInvested * (sellQuantity / portfolio[selectedCompanyForTrade.id]?.shares))).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                </div>
                <div className="modal-actions">
                  <button 
                    className="button cancel-button"
                    onClick={() => setShowSellModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="button confirm-button"
                    onClick={handleSellConfirm}
                    disabled={sellQuantity <= 0 || sellQuantity > (portfolio[selectedCompanyForTrade.id]?.shares || 0)}
                  >
                    💸 Confirm Sale
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {selectedCompany && ReactDOM.createPortal(
          <div className="modal-overlay company-detail-modal" onClick={() => setSelectedCompany(null)}>
            <div className="modal-content company-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="header-content">
                  <div className="company-title">
                    <h2>{selectedCompany.name}</h2>
                    <span className="company-hint">{selectedCompany.hint}</span>
                  </div>
                  <div className="header-actions">
                    <div className="current-price-display">
                      <span className="price-label">Current Price</span>
                      <span className="price-value">₹{selectedCompany.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                    <button 
                      className="modal-close"
                      onClick={() => setSelectedCompany(null)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-body">
                <div className="company-overview">
                  <div className="overview-grid">
                    <div className="overview-card">
                      <div className="card-header">
                        <span className="card-icon">📊</span>
                        <span className="card-title">Price Performance</span>
                      </div>
                      <div className="card-content">
                        <div className="performance-indicator">
                          <CircularProgress 
                            percentage={calculatePercentageChange(selectedCompany)} 
                            size={110} 
                            strokeWidth={12}
                            label="Change"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="overview-card">
                      <div className="card-header">
                        <span className="card-icon">📈</span>
                        <span className="card-title">Price Ranges</span>
                      </div>
                      <div className="card-content">
                        <div className="price-range-item">
                          <span className="range-label">Current</span>
                          <LinearProgress 
                            current={selectedCompany.currentPrice}
                            min={selectedCompany.lowestPrice}
                            max={selectedCompany.highestPrice}
                            label=""
                            color="#667eea"
                          />
                          <span className="range-value">₹{selectedCompany.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                        <div className="price-range-item">
                          <span className="range-label">Highest</span>
                          <LinearProgress 
                            current={selectedCompany.highestPrice}
                            min={selectedCompany.lowestPrice}
                            max={selectedCompany.highestPrice}
                            label=""
                            color="#28a745"
                          />
                          <span className="range-value">₹{selectedCompany.highestPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                        <div className="price-range-item">
                          <span className="range-label">Lowest</span>
                          <LinearProgress 
                            current={selectedCompany.lowestPrice}
                            min={selectedCompany.lowestPrice}
                            max={selectedCompany.highestPrice}
                            label=""
                            color="#dc3545"
                          />
                          <span className="range-value">₹{selectedCompany.lowestPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {selectedCompany.priceHistory && selectedCompany.priceHistory.length > 0 && (
                  <div className="chart-section">
                    <div className="section-header">
                      <h3>📈 Price History</h3>
                      <p>Click on chart points to see detailed information</p>
                    </div>
                    <StockChart 
                      priceHistory={selectedCompany.priceHistory} 
                      companyName={selectedCompany.name}
                      onPointClick={(point) => console.log('Point clicked:', point)}
                      onInvest={(quantity, newCoins) => handleInvestInCompany(selectedCompany, quantity, newCoins)}
                      onSell={(quantity) => sellShares(selectedCompany.id, quantity)}
                      isInvested={!!portfolio[selectedCompany.id]}
                      currentPrice={selectedCompany.currentPrice}
                      userCoins={coins}
                      ownedShares={portfolio[selectedCompany.id]?.shares || 0}
                      onBuyClick={() => {
                        setSelectedCompany(null);
                        setTimeout(() => {
                          handleBuyClick(selectedCompany);
                        }, 150);
                      }}
                      onSellClick={() => {
                        setSelectedCompany(null);
                        setTimeout(() => {
                          handleSellClick(selectedCompany.id);
                        }, 150);
                      }}
                    />
                    <div style={{ marginTop: 32 }}>
                      <LinearProgress
                        current={selectedCompany.currentPrice}
                        min={selectedCompany.lowestPrice}
                        max={selectedCompany.highestPrice}
                        label="Current Price Position"
                        color="#667eea"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* How to Play Section */}
        {showHowToPlay && (
          <div className="card how-to-play-card">
            <h2 style={{ textAlign: 'center', marginBottom: 24 }}>🎮 How to Play GamiStocks</h2>
            <div className="how-to-play-steps">
              <div className="how-to-play-step">
                <span className="how-to-play-icon">💰</span>
                <div>
                  <strong>Start with Coins:</strong> You begin with a set amount of coins. Use them to invest in fictional companies!
                </div>
              </div>
              <div className="how-to-play-step">
                <span className="how-to-play-icon">🔍</span>
                <div>
                  <strong>Explore Companies:</strong> Browse or search all companies. Click a company to see its price history, performance, and details.
                </div>
              </div>
              <div className="how-to-play-step">
                <span className="how-to-play-icon">🛒</span>
                <div>
                  <strong>Buy Stocks:</strong> Invest your coins to buy shares. The price may go up or down each round!
                </div>
              </div>
              <div className="how-to-play-step">
                <span className="how-to-play-icon">🎲</span>
                <div>
                  <strong>Play Rounds:</strong> Click <b>Play Round</b> to simulate the market. All stock prices move—sometimes a little, sometimes a lot!
                </div>
              </div>
              <div className="how-to-play-step">
                <span className="how-to-play-icon">💸</span>
                <div>
                  <strong>Sell Stocks:</strong> Sell your shares at any time to realize profits (or cut losses). Your coins are updated instantly.
                </div>
              </div>
              <div className="how-to-play-step">
                <span className="how-to-play-icon">📈</span>
                <div>
                  <strong>Track Portfolio:</strong> See your holdings, profit/loss, and total value. Try to grow your wealth over time!
                </div>
              </div>
              <div className="how-to-play-step">
                <span className="how-to-play-icon">🔄</span>
                <div>
                  <strong>Repeat & Compete:</strong> Keep playing rounds, investing, and selling. Can you become the top GamiStocks tycoon?
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <button className="button" onClick={() => { setShowHowToPlay(false); setActiveTab('your-stocks'); }}>
                🚀 Start Playing
              </button>
            </div>
          </div>
        )}
      </div>


    </div>
  );
};

export default App; 